import { useContext, useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import LogicContext from '../../Context/LogicContext';
import { formatFormError } from '../../helpers/formartFormError';
import moment from 'moment';
import { GetExpenseListData, GetExpenseEdit, SetExpenseListData, SetExpenseEdit } from '../../store/expensesAtom';
import { accountService } from '../../services/accountService';
import { createModal } from 'react-modal-promise';
import GeneralModal from '../../helpers/GeneralModal';
import { useNavigate } from 'react-router-dom';

// --- Icons ---
const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const SaveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const FileDocIcon = () => (
  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const UploadCloudIcon = () => (
  <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const MAX_FILE_SIZE_MB = 2;

const MultiFileUpload = ({ files, onAdd, onRemove }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      if (f.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
        alert(`"${f.name}" exceeds ${MAX_FILE_SIZE_MB}MB and was skipped.`);
        return false;
      }
      return true;
    });
    onAdd(valid);
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      {/* Drop / click zone */}
      <div
        onClick={() => inputRef.current?.click()}
        className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 hover:border-blue-300 cursor-pointer transition-all"
      >
        <UploadCloudIcon />
        <p className="text-sm text-gray-500 font-medium">Click to select receipts</p>
        <p className="text-xs text-gray-400">JPG, PNG, PDF — max {MAX_FILE_SIZE_MB}MB each</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          className="hidden"
          onChange={handleChange}
        />
      </div>

      {/* File preview grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {files.map((file, idx) => {
            const isImage = file.type.startsWith('image/');
            const previewUrl = isImage ? URL.createObjectURL(file) : null;
            return (
              <div key={idx} className="relative group rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
                <div className="flex items-center justify-center h-24 bg-gray-50">
                  {isImage
                    ? <img src={previewUrl} alt={file.name} className="h-full w-full object-cover" />
                    : <FileDocIcon />
                  }
                </div>
                <p className="text-xs text-gray-500 px-2 py-1.5 truncate" title={file.name}>
                  {file.name}
                </p>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {files.length > 0 && (
        <p className="text-xs text-gray-400">{files.length} file{files.length > 1 ? 's' : ''} selected</p>
      )}
    </div>
  );
};

// --- Styles ---
const inputCls = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm placeholder-gray-400";
const selectCls = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm appearance-none cursor-pointer";

const FormField = ({ label, children, required, error, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider ml-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">{children}</div>
    {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
  </div>
);

const emptyLine = () => ({ expense_account_id: '', amount: '', tax_inclusive_id: '2', tax_id: '', remark: '' });

const buildXmlString = (lines) => {
  const items = lines.map(l =>
    `<a><expense_account_id>${l.expense_account_id}</expense_account_id>` +
    `<amount>${l.amount}</amount>` +
    `<tax_inclusive_id>${l.tax_inclusive_id}</tax_inclusive_id>` +
    `<tax_id>${l.tax_id || ''}</tax_id>` +
    `<remark>${l.remark || ''}</remark></a>`
  ).join('');
  return `<root>${items}</root>`;
};

const AddExpensePage = () => {
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      expense_date: moment().format('YYYY-MM-DD'),
      paid_account_id: '',
      vendor_id: '',
      expense_reference: '',
      expense_remark: '',
      lines: [emptyLine()],
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });
  const watchedLines = watch('lines');

  const logicContext = useContext(LogicContext);
  const myPromiseModal = createModal(GeneralModal);
  const navigate = useNavigate();
  const setExpenseListData = SetExpenseListData();
  const setExpenseEdit = SetExpenseEdit();

  const expenseDataStore = GetExpenseListData();
  const editableExp = GetExpenseEdit();
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]); // already-uploaded URLs on edit
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editableExp?.Ref_No) {
      const mappedLines = editableExp.lines?.length
        ? editableExp.lines.map(line => ({
          expense_account_id: String(line.Expense_Account_Id || ''),
          amount: line.Amount || '',
          tax_inclusive_id: String(line.Tax_Inclusive_Id || '2'),
          tax_id: line.Tax_Id || '',
          remark: line.Expense_Notes || '',
        }))
        : [emptyLine()];

      reset({
        expense_date: moment(editableExp.Expense_Date).format('YYYY-MM-DD'),
        paid_account_id: editableExp.Paid_Account_Id ? String(editableExp.Paid_Account_Id) : '',
        vendor_id: editableExp.Vendor_Id ? String(editableExp.Vendor_Id) : '',
        expense_remark: editableExp.Expense_Remark || '',
        expense_reference: editableExp.Expense_Reference || '',
        lines: mappedLines,
      });

      setExistingAttachments(editableExp.attachmentUrls || []);
      setAttachedFiles([]);
    }
  }, [editableExp]);

  useEffect(() => {
    if (!expenseDataStore.paid_account?.length) {
      getExpensesListFromApi();
    }
  }, [expenseDataStore.paid_account?.length]);

  const getExpensesListFromApi = async () => {
    try {
      const response = await accountService.getExpenseMainList();
      const serverData = response?.data;
      setExpenseListData({
        vendor_info: serverData?.vendor_info || [],
        expense_account: serverData?.expense_account || [],
        expense_info: serverData?.expense_info || [],
        expense_status: serverData?.expense_status || [],
        paid_account: serverData?.paid_account || [],
        tax_info: serverData?.tax_info || [],
      });
    } catch (err) {
      console.error("Failed to fetch expense list", err);
    }
  };

  const onSubmit = async (data) => {
    const result = await myPromiseModal({
      title: 'Confirmation',
      body: editableExp?.Ref_No ? 'Do you want to update this record?' : 'Do you want to save this record?',
      showInput: false,
    });

    if (!result?.decision) return;

    setIsSubmitting(true);
    try {
      const option = editableExp?.Ref_No ? 2 : 1;
      const formData = new FormData();
      formData.append('option', option);
      if (editableExp?.Ref_No) formData.append('ref_no', editableExp.Ref_No);
      formData.append('expense_date', data.expense_date);
      formData.append('paid_account_id', data.paid_account_id);
      formData.append('vendor_id', data.vendor_id || '');
      formData.append('expense_remark', data.expense_remark || '');
      formData.append('expense_reference', data.expense_reference || '');
      formData.append('xml_string', buildXmlString(data.lines));
      attachedFiles.forEach(file => formData.append('files[]', file));
      // Keep existing S3 URLs that the user hasn't removed
      const existingUrlsCsv = existingAttachments.map(a => a.Attachment_URL).join(',');
      if (existingUrlsCsv) formData.append('existing_img_urls', existingUrlsCsv);

      const res = await accountService.submitExpense(formData);
      if (res?.data !== undefined) {
        const { message_text, message_id } = res.data[0] || res.data?.result_status[0];

        if (message_id === 1) {
          logicContext.showToast(message_text, 'success');
          setExpenseEdit({});
          sessionStorage.setItem('reloadPage', 'true');
          navigate(-1);
        } else {
          logicContext.showToast(message_text, 'error');
        }
      }
    } catch (error) {
      logicContext.showToast("An error occurred while saving", 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onErrors = (error) => {
    logicContext.showToast(formatFormError(error), 'error');
  };

  // Grouped expense accounts for select optgroups
  const groupedAccounts = expenseDataStore?.expense_account?.reduce((acc, curr) => {
    const group = curr.Account_Type_Name || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(curr);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 bg-white rounded-full shadow-sm border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
        >
          <ChevronLeft />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {editableExp?.Ref_No ? 'Edit' : 'Add'} Expense
          </h1>
          <p className="text-sm text-gray-500">Record business expense details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onErrors)} className="max-w-5xl mx-auto space-y-6">

        {/* ── Card 1: Header fields ── */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Expense Details</h2>
          </div>
          <div className="p-6 md:p-8 space-y-6">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Date" required error={errors.expense_date?.message}>
                <input
                  type="date"
                  className={inputCls}
                  {...register('expense_date', { required: 'Expense date is required' })}
                />
              </FormField>

              <FormField label="Paid Through" required error={errors.paid_account_id?.message}>
                <select
                  {...register('paid_account_id', { required: 'Paid through account is required' })}
                  className={selectCls}
                >
                  <option value="">Select paid account</option>
                  {expenseDataStore?.paid_account?.map((acc, idx) => (
                    <option key={idx} value={acc.Account_Id}>{acc.Account_Name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </FormField>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Vendor" error={errors.vendor_id?.message}>
                <select {...register('vendor_id')} className={selectCls}>
                  <option value="">Select vendor</option>
                  {expenseDataStore?.vendor_info?.map((v, idx) => (
                    <option key={idx} value={v.Vendor_Id}>{v.Vendor_Name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </FormField>

              <FormField label="Reference #" error={errors.expense_reference?.message}>
                <input
                  type="text"
                  maxLength="500"
                  placeholder="Reference number"
                  className={inputCls}
                  {...register('expense_reference')}
                />
              </FormField>
            </div>

            <FormField label="Expense Note" error={errors.expense_remark?.message}>
              <textarea
                rows={2}
                {...register('expense_remark')}
                className={`${inputCls} resize-none`}
                placeholder="General note about this expense..."
              />
            </FormField>

            <div className="pt-4 border-t border-gray-100">
              <FormField label="Attach Receipts / Files">
                <div className="mt-2 space-y-3">

                  {/* Existing uploaded files (edit mode) */}
                  {existingAttachments.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Already uploaded — remove any you no longer need:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {existingAttachments.map((att, idx) => {
                          const url = att.Attachment_URL;
                          const filename = url?.split('/').pop() || `File ${idx + 1}`;
                          const isImage = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url);
                          return (
                            <div key={idx} className="relative group rounded-lg border border-blue-200 bg-blue-50 overflow-hidden shadow-sm">
                              <div className="flex items-center justify-center h-24 bg-gray-50">
                                {isImage
                                  ? <img src={url} alt={filename} className="h-full w-full object-cover" />
                                  : <FileDocIcon />
                                }
                              </div>
                              <p className="text-xs text-gray-500 px-2 py-1.5 truncate" title={filename}>{filename}</p>
                              <button
                                type="button"
                                onClick={() => setExistingAttachments(prev => prev.filter((_, i) => i !== idx))}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* New files picker */}
                  <MultiFileUpload
                    files={attachedFiles}
                    onAdd={newFiles =>
                      setAttachedFiles(prev => {
                        const seen = new Set(prev.map(f => f.name + f.size));
                        return [...prev, ...newFiles.filter(f => !seen.has(f.name + f.size))];
                      })
                    }
                    onRemove={idx => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))}
                  />
                </div>
              </FormField>
            </div>
          </div>
        </div>

        {/* ── Card 2: Expense line items ── */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Expense Lines</h2>
            <button
              type="button"
              onClick={() => append(emptyLine())}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition"
            >
              <PlusIcon /> Add Line
            </button>
          </div>

          <div className="p-4 md:p-6 space-y-3">
            {/* Column headers */}
            <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_2fr_auto] gap-3 px-1">
              {['Expense Account *', 'Amount *', 'Tax Type *', 'Tax', 'Line Remark', ''].map((h, i) => (
                <span key={i} className="text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</span>
              ))}
            </div>

            {fields.map((field, index) => {
              const rowTaxType = watchedLines?.[index]?.tax_inclusive_id;
              return (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_2fr_auto] gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
                >
                  {/* Expense Account */}
                  <div className="relative">
                    <select
                      {...register(`lines.${index}.expense_account_id`, { required: 'Required' })}
                      className={selectCls}
                    >
                      <option value="">Select account</option>
                      {groupedAccounts && Object.entries(groupedAccounts).map(([groupName, accounts]) => (
                        <optgroup key={groupName} label={groupName}>
                          {accounts.map(acc => (
                            <option key={acc.Account_Id} value={acc.Account_Id}>{acc.Account_Name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                    {errors.lines?.[index]?.expense_account_id && (
                      <p className="text-xs text-red-500 mt-1">{errors.lines[index].expense_account_id.message}</p>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      className={inputCls}
                      {...register(`lines.${index}.amount`, {
                        required: 'Required',
                        validate: v => parseFloat(v) > 0 || 'Must be > 0',
                      })}
                    />
                    {errors.lines?.[index]?.amount && (
                      <p className="text-xs text-red-500 mt-1">{errors.lines[index].amount.message}</p>
                    )}
                  </div>

                  {/* Tax Type */}
                  <div>
                    <select
                      {...register(`lines.${index}.tax_inclusive_id`, { required: 'Required' })}
                      className={selectCls}
                    >
                      <option value="2">Tax Exclusive</option>
                      <option value="1">Tax Inclusive</option>
                    </select>
                  </div>

                  {/* Tax (conditional) */}
                  <div>
                    {rowTaxType === '1' ? (
                      <div className="relative">
                        <select {...register(`lines.${index}.tax_id`)} className={selectCls}>
                          <option value="">Select tax</option>
                          {expenseDataStore?.tax_info?.map((tax, idx) => (
                            <option key={idx} value={tax.Tax_Id}>{tax.Tax_Name}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    ) : (
                      <div className="h-9 flex items-center px-3 text-xs text-gray-400 bg-gray-100 rounded-lg border border-gray-200">N/A</div>
                    )}
                  </div>

                  {/* Line Remark */}
                  <div>
                    <input
                      type="text"
                      placeholder="Line note..."
                      maxLength={500}
                      className={inputCls}
                      {...register(`lines.${index}.remark`)}
                    />
                  </div>

                  {/* Remove row */}
                  <div className="flex items-start justify-center pt-1">
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove line"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="bg-white rounded-2xl shadow border border-gray-100 px-6 py-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200"
          >
            <SaveIcon />
            {isSubmitting ? 'Saving...' : editableExp?.Ref_No ? 'Update Record' : 'Save Record'}
          </button>
        </div>

        <div className="text-center text-xs text-gray-400 pb-4">
          <p>Fields marked with <span className="text-red-500 font-bold">*</span> are required.</p>
        </div>
      </form>
    </div>
  );
};

export default AddExpensePage;
